import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Row, Table } from 'react-bootstrap'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  useBulkDeleteUsersMutation,
  useGetTokenByIDMutation,
  useGetUsersQuery,
  useSendExcelFileParentMutation,
  useSendExcelFileStudentMutation,
  useSendExcelFileTeacherMutation
} from '../../store/api/users/users.api'
import Pagination from '../../ui/Pagination/Pagination'
import UsersFilters from '../../ui/filter/UsersFilters'
import { searchParamsData } from '../../utils/searchParamsData'
import Loading from '../Loading/Loading'
import UsersTable from './UsersTable/UsersTable'
import { CustomMessageSuccsesOrError } from '../../utils/CustomMessageSuccsesOrError'
import {
  APP_URL,
  BASE_URL,
  DefaultParams,
  EXCEL_FORMAT,
  PAGESIZE,
  USER_ROLE
} from '../../utils/constants'
import RemoveModalQuestion from '../../ui/RemoveModalQuestion/RemoveModalQuestion'
import { UseToggleModalHOC } from '../../hooks/toggleModal'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { responseError } from '../../validation/validationRequaired'

const Users = (): JSX.Element => {
  const { t: tGeneral } = useTranslation('general')
  const { t: tSideBar } = useTranslation('sideBar')
  const { t: tMessages } = useTranslation('messages')
  const inputRef = useRef<HTMLInputElement>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [roleId, setRoleId] = useState<string>('')
  const [sendExcelStudent] = useSendExcelFileStudentMutation()
  const [sendExcelTeacher] = useSendExcelFileTeacherMutation()
  const [sendExcelFileParent] = useSendExcelFileParentMutation()
  const [bulkDeleteUsers] = useBulkDeleteUsersMutation()
  const [usersIds, setUsersIds] = useState<number[]>([])
  const { isModalVisible, toggleModal } = UseToggleModalHOC()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [getTokenById] = useGetTokenByIDMutation()
  const {
    isLoading: isUserLoading,
    data: users,
    refetch
  } = useGetUsersQuery({
    ...searchParamsData(searchParams, { ...DefaultParams })
  })

  useEffect(() => {
    refetch()
  }, [searchParams])

  const handleExcelFile = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const excelUploadHandlers = {
      [USER_ROLE.Student]: sendExcelStudent,
      [USER_ROLE.Teacher]: sendExcelTeacher,
      [USER_ROLE.Parent]: sendExcelFileParent
    } as const

    const selectedFile = event.target.files?.[0]
    const formData = new FormData()
    if (selectedFile) {
      setIsLoading(true)
      try {
        formData.append('file', selectedFile)
        const uploadHandler = excelUploadHandlers[roleId]

        const res = await uploadHandler(formData)
        CustomMessageSuccsesOrError(res)
        refetch()
      } catch (error) {
        CustomMessageSuccsesOrError(error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    const pathName = location.pathname.split('/')
    const currentRoleId = pathName[pathName.length - 1].toString()
    if (currentRoleId.length === 1) {
      setRoleId(currentRoleId)
      searchParams.set('roleId', currentRoleId)
    } else {
      searchParams.delete('roleId')
      setRoleId('')
    }
  }, [location.pathname])

  const tableName = (roleId: string): string => {
    if (roleId === USER_ROLE.Admin) {
      return tGeneral('leaderShips')
    } else if (roleId === USER_ROLE.Student) {
      return tSideBar('students')
    } else if (roleId === USER_ROLE.Teacher) {
      return tSideBar('teachers')
    } else if (roleId === USER_ROLE.Parent) {
      return tSideBar('parents')
    } else if (roleId === '') {
      return tSideBar('users')
    }
  }

  const handleDeleteUsersByIds = async (): Promise<void> => {
    await bulkDeleteUsers(usersIds)
    toast.success(tMessages('usersRemovedSuccessfully'))
    toggleModal()
    refetch()
    setUsersIds([])
  }

  const handleCheckboxChange = (id: number): void => {
    if (!usersIds.includes(id)) {
      setUsersIds(prev => [...prev, id])
    } else {
      setUsersIds(prev => prev.filter(resourceId => resourceId !== id))
    }
  }

  const handleSelectAll = (checked: boolean): void => {
    if (checked) {
      const ids = users.data.map(user => user.id)
      setUsersIds(ids)
      return
    }
    setUsersIds([])
  }

  const handleImpersonateUser = async (id: number): Promise<void> => {
    try {
      const res = await getTokenById(id)
      const token = (res as unknown as { data }).data.token as string
      if (token) {
        window.open(`${APP_URL}impersonate?token=${token}`)
      }
    } catch (error) {
      responseError(error)
    }
  }
  console.log(typeof roleId, 'roleId')
  return (
    <div>
      {isUserLoading && <Loading />}
      <UsersFilters setQuery={setSearchParams} query={searchParams} role={roleId} />
      <Row className=' row-sm'>
        <Col lg={12}>
          <Card className='custom-card'>
            <Card.Header>
              <Col xs={6} md={10}>
                <h3 className='card-title mb-1'>{tableName(roleId)}</h3>
              </Col>
              <Col className='d-flex'>
                {roleId === USER_ROLE.Student ? (
                  <>
                    <div className='mx-1'>
                      <a
                        download
                        href={`${BASE_URL}admin/export-user-data`}
                        className='btn btn-success'
                        target='_blank'
                      >
                        {tGeneral('export')}
                      </a>
                    </div>
                    <button
                      className='btn btn-danger mx-1'
                      onClick={() => usersIds.length && toggleModal()}
                    >
                      {tGeneral('deleteUsers')}
                    </button>
                  </>
                ) : null}
                {roleId !== USER_ROLE.Admin && roleId ? (
                  <div className='mx-3' style={isLoading ? { pointerEvents: 'none' } : {}}>
                    <input
                      ref={inputRef}
                      onChange={e => handleExcelFile(e)}
                      className='d-none'
                      type='file'
                      accept={EXCEL_FORMAT}
                    />
                    <button
                      type='button'
                      onClick={() => inputRef?.current.click()}
                      className='btn btn-success'
                    >
                      <span>
                        <i className='fe fe-log-in'></i>&nbsp;
                      </span>
                      {roleId === USER_ROLE.Teacher ? tGeneral('importTeacher') : null}
                      {roleId === USER_ROLE.Student ? tGeneral('importStudent') : null}
                      {roleId === USER_ROLE.Parent ? tGeneral('importParent') : null}
                    </button>
                  </div>
                ) : null}
                <Button onClick={() => navigate('/users/create')}>Create User</Button>
              </Col>
            </Card.Header>
            <Card.Body>
              <div className='table-responsive'>
                <Table className='table table-bordered text-nowrap text-md-nowrap mg-b-0'>
                  <thead>
                    <tr className='text-center'>
                      {roleId === USER_ROLE.Student ? (
                        <th>
                          <input
                            type='checkbox'
                            onChange={({ target }) => handleSelectAll(target.checked)}
                            checked={Boolean(usersIds.length)}
                          />
                        </th>
                      ) : null}
                      <th>{tGeneral('id')}</th>
                      <th>{tGeneral('fullName')}</th>
                      <th>{tGeneral('email')}</th>
                      <th>{tGeneral('status')}</th>
                      <th>{tGeneral('package')}</th>
                      <th>{tGeneral('verify')}</th>
                      <th>{tGeneral('createdAt')}</th>
                      <th>{tGeneral('action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users?.data.length > 0 &&
                      users?.data.map(user => {
                        return (
                          <UsersTable
                            key={user.id}
                            user={user}
                            refetch={refetch}
                            roleId={roleId}
                            handleCheckboxChange={handleCheckboxChange}
                            usersIds={usersIds}
                            handleImpersonateUser={handleImpersonateUser}
                          />
                        )
                      })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Pagination
              setQuery={setSearchParams}
              numPages={Math.ceil(users?.count / PAGESIZE)}
              query={searchParams}
            />
          </Card>
        </Col>
      </Row>
      {isModalVisible ? (
        <RemoveModalQuestion fetchDeleteByIds={handleDeleteUsersByIds} toggleModal={toggleModal} />
      ) : null}
    </div>
  )
}

export default Users
